import { ArrowRight, Check } from "lucide-react";
import { useInView } from "@/lib/hooks/useInView";
import { BrandMark } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

interface CellProps {
  title: string;
  text: string;
  className?: string;
  children?: React.ReactNode;
}

function Cell({ title, text, className, children }: CellProps): JSX.Element {
  return (
    <article className={cn("lp-lift flex flex-col rounded-2xl border border-border bg-surface p-6", className)}>
      <h3 className="text-lg font-semibold tracking-[-0.02em]">{title}</h3>
      <p className="mt-2 max-w-[44ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{text}</p>
      {children}
    </article>
  );
}

const ORIGINS = ["Grupo de ofertas", "Canal de cupons", "Achadinhos"] as const;
const DESTINATIONS = ["Seu Telegram", "Seu WhatsApp"] as const;

function ForwardingVisual(): JSX.Element {
  return (
    <div className="mt-auto grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 pt-10 sm:gap-6">
      <ul className="space-y-2">
        {ORIGINS.map((name) => (
          <li key={name} className="truncate rounded-lg border border-border bg-background/70 px-3 py-2 text-xs text-muted-foreground sm:text-sm">
            {name}
          </li>
        ))}
      </ul>
      <div className="flex flex-col items-center gap-2 text-muted-foreground" aria-hidden="true">
        <ArrowRight size={16} />
        <BrandMark className="h-10 w-10" />
        <ArrowRight size={16} />
      </div>
      <ul className="space-y-2">
        {DESTINATIONS.map((name) => (
          <li key={name} className="truncate rounded-lg border border-signal/40 bg-signal-dim/50 px-3 py-2 text-xs text-foreground sm:text-sm">
            {name}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Formato de cliques por hora, so ilustrativo (rotulado como exemplo). */
const CLICK_SHAPE = [0.18, 0.12, 0.1, 0.22, 0.38, 0.52, 0.46, 0.61, 0.84, 1, 0.72, 0.58] as const;

function ClicksVisual(): JSX.Element {
  const { ref, isInView } = useInView<HTMLDivElement>();
  return (
    <div ref={ref} className="mt-auto pt-6">
      <div className="flex h-20 items-end gap-1.5" aria-hidden="true">
        {CLICK_SHAPE.map((value, index) => (
          <span
            key={index}
            className="lp-bar flex-1 rounded-sm bg-signal/85"
            data-on={isInView}
            style={{ height: "100%", "--lp-bar-scale": value, transitionDelay: `${index * 40}ms` } as React.CSSProperties}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Cliques por hora (exemplo)</p>
    </div>
  );
}

const SHOWCASE = ["Fone bluetooth", "Air fryer 4 L", "Kit camisetas"] as const;

function ShowcaseVisual(): JSX.Element {
  return (
    <ul className="mt-auto space-y-2 pt-6" aria-hidden="true">
      {SHOWCASE.map((name) => (
        <li key={name} className="flex items-center gap-3 rounded-lg border border-border bg-background/70 p-2 pr-3">
          <span className="h-8 w-8 shrink-0 rounded-md bg-surface-overlay" />
          <span className="flex-1 truncate text-sm">{name}</span>
          <span className="rounded-md bg-signal px-2 py-0.5 text-xs font-semibold text-signal-foreground">Ver oferta</span>
        </li>
      ))}
    </ul>
  );
}

const REPORT_SOURCES = ["Shopee", "AWIN"] as const;

function ReportVisual(): JSX.Element {
  return (
    <ul className="mt-auto space-y-2 pt-6" aria-hidden="true">
      {REPORT_SOURCES.map((source) => (
        <li key={source} className="flex items-center justify-between rounded-lg border border-border bg-background/70 px-3 py-2.5">
          <span className="text-sm font-medium">{source}</span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Check size={14} className="text-signal" />
            Relatório oficial
          </span>
        </li>
      ))}
    </ul>
  );
}

const SCHEDULE = [
  { time: "09:00", label: "Ofertas da manhã" },
  { time: "12:30", label: "Cupons do almoço" },
  { time: "20:00", label: "Relâmpago da noite" },
] as const;

function ScheduleVisual(): JSX.Element {
  return (
    <ol className="mt-auto space-y-3 pt-6" aria-hidden="true">
      {SCHEDULE.map((slot) => (
        <li key={slot.time} className="flex items-baseline gap-4 border-l border-border-strong pl-4">
          <span className="font-mono text-sm tabular-nums text-signal">{slot.time}</span>
          <span className="text-sm text-foreground/90">{slot.label}</span>
        </li>
      ))}
    </ol>
  );
}

export function Recursos(): JSX.Element {
  return (
    <section id="recursos" aria-labelledby="recursos-heading" className="scroll-mt-20 border-t border-border">
      <div className="mx-auto max-w-[77.5rem] px-4 py-24 sm:px-6 lg:px-8">
        <h2
          id="recursos-heading"
          className="max-w-[20ch] text-[clamp(2rem,1.4rem+2.2vw,3.25rem)] font-bold leading-[1.05] tracking-[-0.035em]"
        >
          Tudo o que acontece depois do link.
        </h2>

        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <Cell
            title="Encaminhamento entre grupos"
            text="Escolha de onde vêm as ofertas e para onde vão. O bot copia, troca o link e publica, sem loop e sem repetir oferta."
            className="md:col-span-2 lg:col-span-4 lg:row-span-2"
          >
            <ForwardingVisual />
          </Cell>
          <Cell
            title="Rodízio de grupos"
            text="Um link só que manda cada clique para o próximo grupo de WhatsApp da lista, até 10 grupos."
            className="lg:col-span-2"
          />
          <Cell
            title="Encurtador com clique rastreado"
            text="Nas lojas que permitem, cada link curto conta seus cliques."
            className="lg:col-span-2"
          >
            <ClicksVisual />
          </Cell>
          <Cell
            title="Comissões oficiais"
            text="Shopee e AWIN mandam os números direto para o seu painel, por loja e por período."
            className="lg:col-span-2"
          >
            <ReportVisual />
          </Cell>
          <Cell
            title="Vitrine e link na bio"
            text="Uma página com suas ofertas e outra com seus links, prontas para o Instagram e o TikTok."
            className="lg:col-span-2"
          >
            <ShowcaseVisual />
          </Cell>
          <Cell
            title="Disparos agendados"
            text="Envie agora, agende ou deixe recorrente, para o Telegram e o WhatsApp."
            className="lg:col-span-2"
          >
            <ScheduleVisual />
          </Cell>
          <Cell
            title="IA para texto e imagem"
            text="Copy de venda pronta e foto do produto em cena, com a sua marca d'água aplicada."
            className="border-signal/30 bg-signal-dim/40 lg:col-span-3"
          />
          <Cell
            title="Curadoria de ofertas"
            text="Um feed de ofertas com o seu link na hora e promoções automáticas no ritmo que você escolher."
            className="lg:col-span-3"
          />
        </div>
      </div>
    </section>
  );
}
